import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import NanoEvents from 'nanoevents';
import nano from 'nanoid';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { jsx } from '@emotion/core';
import { useState, useCallback, useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';

/**
 * The node view portal container keeps track of all the portals which have been added by react to render
 * the node views in the editor.
 */
var PortalContainer =
/*#__PURE__*/
function () {
  function PortalContainer() {
    var _this = this;

    _classCallCheck(this, PortalContainer);

    _defineProperty(this, "portals", new Map());

    _defineProperty(this, "events", new NanoEvents());

    _defineProperty(this, "on", function (callback) {
      return _this.events.on('update', callback);
    });
  }

  _createClass(PortalContainer, [{
    key: "update",

    /**
     * Trigger an update in all subscribers.
     */
    value: function update() {
      this.events.emit('update', this.portals);
    }
    /**
     * Responsible for registering a new portal by rendering the react element into the provided container.
     */

  }, {
    key: "render",
    value: function render(_ref) {
      var _render = _ref.render,
          container = _ref.container;
      var portal = this.portals.get(container);
      var key = portal ? portal.key : nano();
      this.portals.set(container, {
        render: _render,
        key: key
      });
      this.update();
    }
    /**
     * Force an update in all the portals by setting new keys for every portal.
     *
     * Delete all orphaned containers (deleted from the DOM). This is useful for
     * Decoration where there is no destroy method.
     */

  }, {
    key: "forceUpdate",
    value: function forceUpdate() {
      var _this2 = this;

      this.portals.forEach(function (_ref2, container) {
        var render = _ref2.render;

        // Assign the portal a new key so it is re-rendered
        _this2.portals.set(container, {
          render: render,
          key: nano()
        });
      });
      this.update();
    }
    /**
     * Deletes the portal within the container.
     */

  }, {
    key: "remove",
    value: function remove(container) {
      this.portals.delete(container);
      this.update();
    }
  }]);

  return PortalContainer;
}();

/**
 * The component that places all the portals into the DOM.
 *
 * Portals can be created by NodeView and also the static
 * widget method on Decorations.
 */
var RemirrorPortals = function RemirrorPortals(_ref) {
  var portalContainer = _ref.portalContainer;

  var _useState = useState(Array.from(portalContainer.portals.entries())),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];
  /**
   * Update the state whenever the portal is updated.
   */


  var onPortalChange = useCallback(function (portalMap) {
    setState(Array.from(portalMap.entries()));
  }, []);
  useEffect(function () {
    // Auto disposed when the component un-mounts
    return portalContainer.on(onPortalChange);
  }, [onPortalChange, portalContainer]);
  return jsx(Fragment, null, state.map(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        container = _ref3[0],
        _ref3$ = _ref3[1],
        Component = _ref3$.render,
        key = _ref3$.key;

    return jsx(Fragment, {
      key: key
    }, createPortal(jsx(Portal, {
      container: container,
      Component: Component,
      portalContainer: portalContainer
    }), container));
  }));
};

/**
 * This is the component rendered by the createPortal method within the
 * RemirrorPortals component. It is responsible for cleanup when the container
 * is removed from the DOM.
 */
var Portal = function Portal(_ref4) {
  var portalContainer = _ref4.portalContainer,
      container = _ref4.container,
      Component = _ref4.Component;
  useEffect(function () {
    /**
     * Remove the portal container entry when this portal is unmounted.
     * Portals are unmounted when their host container is removed from the dom.
     */
    return function () {
      portalContainer.remove(container);
    };
  }, [container, portalContainer]);
  return jsx(Component, null);
};

export { PortalContainer, RemirrorPortals };
//# sourceMappingURL=react-portals.esm.js.map
